import {
  type Attributes,
  type Context,
  type Link,
  type SpanKind,
} from '@opentelemetry/api';
import {
  AlwaysOffSampler,
  AlwaysOnSampler,
  ParentBasedSampler,
  SamplingDecision,
  TraceIdRatioBasedSampler,
  type Sampler,
  type SamplingResult,
  type Span,
  type SpanProcessor,
} from '@opentelemetry/sdk-trace-base';
import { ATTR_CLIENT_ADDRESS } from '@opentelemetry/semantic-conventions';
import { ATTR_USER_ID } from '@opentelemetry/semantic-conventions/incubating';

import { userContext } from './user-context';

/**
 * A sampler wrapper that always samples error spans regardless of the base sampler's decision.
 * This ensures we never miss error traces even with low sampling rates in production.
 *
 * ## Limitation (Head-based Sampling)
 *
 * This sampler makes decisions at span START time. For HTTP spans, `http.status_code`
 * is typically set at span END, so it won't be available during sampling decision.
 *
 * This sampler IS effective for:
 * - Spans started with `error: true` attribute
 * - Spans started with `exception.type` attribute
 * - Manual spans where error is known upfront
 *
 * For HTTP error sampling, consider:
 * - Tail-based sampling in your collector/proxy (recommended)
 * - Using AdaptiveSampler with URL patterns for critical paths
 *
 * @see https://opentelemetry.io/docs/concepts/sampling/
 */
class ErrorAwareSampler implements Sampler {
  constructor(private baseSampler: Sampler) {}

  shouldSample(
    context: Context,
    traceId: string,
    spanName: string,
    spanKind: SpanKind,
    attributes: Attributes,
    links: Link[],
  ): SamplingResult {
    if (attributes['error'] || attributes['exception.type']) {
      return {
        decision: SamplingDecision.RECORD_AND_SAMPLED,
        attributes: { 'sampling.reason': 'error_aware' },
      };
    }
    return this.baseSampler.shouldSample(
      context,
      traceId,
      spanName,
      spanKind,
      attributes,
      links,
    );
  }

  toString(): string {
    return `ErrorAwareSampler{${this.baseSampler.toString()}}`;
  }
}

/**
 * Sampling rules for AdaptiveSampler.
 * Rules are evaluated in order; first match wins.
 */
export interface SamplingRule {
  pattern: RegExp;
  rate: number;
  name: string;
}

/**
 * Built-in sampling rules for common infrastructure patterns.
 * These are noise reduction rules that apply to most web applications.
 *
 * Note: Business-critical paths (e.g., /api/order) should be defined by
 * the consuming application via `samplingRules` config option.
 */
export const BUILTIN_SAMPLING_RULES: SamplingRule[] = [
  { pattern: /\/health|\/ready|\/live|\/ping/, rate: 0, name: 'health_check' },
  {
    pattern: /\.(js|css|png|jpg|jpeg|gif|svg|woff2?|ttf|ico)(\?|$)/,
    rate: 0.05, // 5% sampling for static assets
    name: 'static_asset',
  },
];

/**
 * @deprecated Use BUILTIN_SAMPLING_RULES instead. Will be removed in v2.0.
 * Business-critical paths should be configured via setupTelemetry options.
 */
export const DEFAULT_SAMPLING_RULES = BUILTIN_SAMPLING_RULES;

/**
 * AdaptiveSampler - URL pattern-based sampling for intelligent cost control.
 *
 * Different URL patterns have different observability value:
 * - Payment/order endpoints: always sample (business critical)
 * - Health checks: never sample (just noise)
 * - Static assets: rarely sample (low value)
 *
 * @example
 * ```typescript
 * // Consumer provides their critical paths
 * const sampler = new AdaptiveSampler(0.1, [
 *   { pattern: /\/api\/order/, rate: 1.0, name: 'orders' },
 *   { pattern: /\/api\/payment/, rate: 1.0, name: 'payments' },
 * ]);
 *
 * // Built-in rules (health checks, static assets) are automatically appended
 * ```
 */
export class AdaptiveSampler implements Sampler {
  private rules: SamplingRule[];
  private defaultRate: number;

  /**
   * @param defaultRate - Default sampling rate for unmatched URLs (0.0 - 1.0)
   * @param customRules - App-specific sampling rules (evaluated first, before built-in rules)
   * @param includeBuiltinRules - Whether to append BUILTIN_SAMPLING_RULES (default: true)
   */
  constructor(
    defaultRate: number = 0.1,
    customRules: SamplingRule[] = [],
    includeBuiltinRules: boolean = true,
  ) {
    this.defaultRate = defaultRate;
    this.rules = includeBuiltinRules
      ? [...customRules, ...BUILTIN_SAMPLING_RULES]
      : customRules;
  }

  shouldSample(
    _context: Context,
    traceId: string,
    spanName: string,
    _spanKind: SpanKind,
    attributes: Attributes,
    _links: Link[],
  ): SamplingResult {
    const url = this.extractUrl(attributes, spanName);

    for (const rule of this.rules) {
      if (rule.pattern.test(url)) {
        return this.makeSamplingDecision(rule.rate, rule.name, traceId);
      }
    }

    return this.makeSamplingDecision(this.defaultRate, 'default', traceId);
  }

  private extractUrl(attributes: Attributes, spanName: string): string {
    return (
      (attributes['http.url'] as string) ||
      (attributes['url.full'] as string) ||
      (attributes['http.target'] as string) ||
      spanName ||
      ''
    );
  }

  /**
   * Uses deterministic sampling based on trace ID for consistent sampling
   * decisions across service boundaries. Falls back to random sampling for
   * invalid trace IDs.
   */
  private makeSamplingDecision(
    rate: number,
    ruleName: string,
    traceId: string,
  ): SamplingResult {
    const shouldSample = this.deterministicSample(traceId, rate);
    return {
      decision: shouldSample
        ? SamplingDecision.RECORD_AND_SAMPLED
        : SamplingDecision.NOT_RECORD,
      attributes: {
        'sampling.rule': ruleName,
        'sampling.rate': rate,
      },
    };
  }

  /**
   * Deterministic sampling using trace ID.
   * For valid 32-char hex trace IDs: uses first 13 chars for deterministic decision.
   * For invalid trace IDs: falls back to Math.random() for probabilistic sampling.
   */
  private deterministicSample(traceId: string, rate: number): boolean {
    if (rate >= 1) return true;
    if (rate <= 0) return false;

    if (this.isValidHexTraceId(traceId)) {
      const hexPart = traceId.slice(0, 13);
      const parsed = parseInt(hexPart, 16);
      const maxValue = 0x1fffffffffffff; // 2^53 - 1 (Number.MAX_SAFE_INTEGER)
      return parsed / maxValue < rate;
    }

    return Math.random() < rate;
  }

  private isValidHexTraceId(traceId: string): boolean {
    return traceId.length >= 16 && /^[0-9a-fA-F]+$/.test(traceId.slice(0, 16));
  }

  toString(): string {
    return `AdaptiveSampler{defaultRate=${this.defaultRate}, rules=${this.rules.length}}`;
  }
}

/**
 * Creates a sampler based on configuration type, wrapped with ErrorAwareSampler
 * to ensure error spans are always captured.
 */
export function createSampler(
  type:
    | 'always_on'
    | 'always_off'
    | 'traceidratio'
    | 'parentbased_always_on'
    | 'parentbased_always_off'
    | 'parentbased_traceidratio',
  arg: number = 0.1,
): Sampler {
  let baseSampler: Sampler;

  switch (type) {
    case 'always_on':
      return new AlwaysOnSampler();
    case 'always_off':
      baseSampler = new AlwaysOffSampler();
      break;
    case 'traceidratio':
      baseSampler = new TraceIdRatioBasedSampler(arg);
      break;
    case 'parentbased_always_on':
      return new ParentBasedSampler({ root: new AlwaysOnSampler() });
    case 'parentbased_always_off':
      baseSampler = new ParentBasedSampler({ root: new AlwaysOffSampler() });
      break;
    case 'parentbased_traceidratio':
      baseSampler = new ParentBasedSampler({
        root: new TraceIdRatioBasedSampler(arg),
      });
      break;
    default:
      return new AlwaysOnSampler();
  }

  return new ErrorAwareSampler(baseSampler);
}

/**
 * UserContextSpanProcessor
 *
 * A span processor that adds user context (UUID, IP) to span attributes.
 * This allows filtering and grouping telemetry data by user.
 *
 * Uses OpenTelemetry semantic conventions:
 * - user.id (ATTR_USER_ID) - User identifier
 * - client.address (ATTR_CLIENT_ADDRESS) - Client IP address
 *
 * Note: We use span attributes (not resource attributes) because:
 * - Resource is created once at TracerProvider init and is immutable
 * - User context changes dynamically (login/logout)
 * - Span attributes are per-span, allowing user context to change over time
 *
 * @example
 * ```typescript
 * const processor = new UserContextSpanProcessor();
 * tracerProvider.addSpanProcessor(processor);
 *
 * // Later, when user logs in
 * setUser({ uuid: user.uuid, ip: userState.ip });
 * // All subsequent spans will include user.id and client.address attributes
 * ```
 */
export class UserContextSpanProcessor implements SpanProcessor {
  /**
   * Called when a span is started
   * Add user context to span attributes using OpenTelemetry semantic conventions
   */
  onStart(span: Span, _parentContext: Context): void {
    if (userContext.uuid) {
      span.setAttribute(ATTR_USER_ID, userContext.uuid);
    }

    if (userContext.ip) {
      span.setAttribute(ATTR_CLIENT_ADDRESS, userContext.ip);
    }
  }

  onEnd(): void {
    // No-op
  }

  shutdown(): Promise<void> {
    return Promise.resolve();
  }

  forceFlush(): Promise<void> {
    return Promise.resolve();
  }
}
